// assets/custom-zoom.js
document.addEventListener("DOMContentLoaded", () => {
  const section = document.querySelector(".section--custom-zoom");
  if (!section) return;

  const lightbox = document.getElementById("custom-lightbox");
  const stage = document.getElementById("custom-lightbox-stage");
  const panEl = document.getElementById("custom-lightbox-pan");
  const imgEl = document.getElementById("custom-lightbox-image");
  const pageEl = document.getElementById("custom-lightbox-page");

  const btnPrev = document.getElementById("custom-lightbox-prev");
  const btnNext = document.getElementById("custom-lightbox-next");
  const btnClose = document.querySelector(".custom-lightbox-close");

  if (!lightbox || !stage || !panEl || !imgEl || !btnPrev || !btnNext || !btnClose) return;

  // ----------------------------
  // Collect UNIQUE images
  // ----------------------------
  function normalizeImgSrc(src) {
    if (!src) return "";
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.delete("width");
      const v = url.searchParams.get("v");
      return url.origin + url.pathname + (v ? `?v=${v}` : "");
    } catch {
      return src.split("?")[0];
    }
  }

  function collectUniqueImages() {
    let candidates = Array.from(section.querySelectorAll("[data-media-id] img"));
    if (!candidates.length) {
      candidates = Array.from(
        section.querySelectorAll(".product__media img, .product__media-wrapper img")
      );
    }

    const seen = new Set();
    const unique = [];

    for (const img of candidates) {
      const src = img.currentSrc || img.src;
      const key = normalizeImgSrc(src);
      if (!key) continue;
      if (seen.has(key)) continue;

      seen.add(key);
      unique.push({ src, alt: img.alt || "" });
    }
    return unique;
  }

  const images = collectUniqueImages();
  if (!images.length) return;

  // Click targets
  const openers = section.querySelectorAll(
    "modal-opener.product__modal-opener, .product__modal-opener, [data-media-id]"
  );

  function findIndexForOpener(opener) {
    const img = opener.querySelector("img");
    if (!img) return 0;
    const key = normalizeImgSrc(img.currentSrc || img.src);
    const idx = images.findIndex((x) => normalizeImgSrc(x.src) === key);
    return idx >= 0 ? idx : 0;
  }

  // ----------------------------
  // State
  // ----------------------------
  let currentIndex = 0;
  let isOpen = false;

  let scale = 1;

  // MIN_SCALE = contain (fully visible)
  let MIN_SCALE = 1;

  // BASE_SCALE = fit-width (default start)
  let BASE_SCALE = 1;

  const MAX_SCALE = 4;
  const STEP = 0.25;

  let offsetX = 0;
  let offsetY = 0;

  let baseW = 0;
  let baseH = 0;

  let dragging = false;
  let activePointerId = null;
  let lastX = 0;
  let lastY = 0;

  // ----------------------------
  // Helpers
  // ----------------------------
  function getStageSize() {
    const r = stage.getBoundingClientRect();
    return { w: r.width || stage.clientWidth, h: r.height || stage.clientHeight };
  }

  function getScaledSize() {
    return { w: baseW * scale, h: baseH * scale };
  }

  function updatePageIndicator() {
    if (!pageEl) return;
    pageEl.textContent = `${currentIndex + 1} / ${images.length}`;
  }

  function canPan() {
    const { w: stageW, h: stageH } = getStageSize();
    const { w: imgW, h: imgH } = getScaledSize();
    return (imgW > stageW + 1) || (imgH > stageH + 1);
  }

  function clampOffsets() {
    const { w: stageW, h: stageH } = getStageSize();
    const { w: imgW, h: imgH } = getScaledSize();
    if (!stageW || !stageH || !imgW || !imgH) return;

    // X: center if smaller, else clamp
    if (imgW <= stageW) {
      offsetX = 0;
    } else {
      const maxX = (imgW - stageW) / 2;
      offsetX = Math.min(maxX, Math.max(-maxX, offsetX));
    }

    // Y: top-lock
    if (imgH <= stageH) {
      offsetY = 0;
    } else {
      const minY = stageH - imgH;
      const maxY = 0;
      offsetY = Math.min(maxY, Math.max(minY, offsetY));
    }
  }

  function applyTransform() {
    panEl.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
    imgEl.style.transform = `scale(${scale})`;

    stage.classList.toggle("is-dragging", dragging);
    stage.style.cursor = dragging ? "grabbing" : (canPan() ? "grab" : "default");
  }

  function snapBack() {
    clampOffsets();
    panEl.style.transition = "transform 160ms ease";
    requestAnimationFrame(() => {
      applyTransform();
      window.setTimeout(() => (panEl.style.transition = ""), 180);
    });
  }

  // ✅ Use NATURAL size and compute BOTH contain + fit-width
  function measureScales() {
    baseW = imgEl.naturalWidth || 1;
    baseH = imgEl.naturalHeight || 1;

    const { w: stageW, h: stageH } = getStageSize();
    if (!stageW || !stageH) {
      MIN_SCALE = 1;
      BASE_SCALE = 1;
      return;
    }

    const contain = Math.min(stageW / baseW, stageH / baseH);
    const fitWidth = stageW / baseW;

    MIN_SCALE = Math.max(0.05, contain);          // zoom-out limit (full image visible)
    BASE_SCALE = Math.max(MIN_SCALE, fitWidth);   // default start (not zoomed out)
  }

  // ----------------------------
  // Zoom
  // ----------------------------
  function zoomTo(newScale) {
    newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
    if (newScale === scale) return;

    scale = newScale;
    clampOffsets();
    applyTransform();
  }

  function zoomIn() { zoomTo(scale + STEP); }
  function zoomOut() { zoomTo(scale - STEP); }

  // ----------------------------
  // Image change
  // ----------------------------
  function setImage(index) {
    currentIndex = (index + images.length) % images.length;
    updatePageIndicator();

    // reset
    scale = 1;
    offsetX = 0;
    offsetY = 0;
    applyTransform();

    imgEl.onload = () => {
      requestAnimationFrame(() => {
        measureScales();

        // ✅ Start at fit-width (bigger than contain => not "zoomed out")
        scale = BASE_SCALE;

        offsetX = 0;
        offsetY = 0;
        clampOffsets();
        applyTransform();
        updatePageIndicator();
      });
    };

    imgEl.src = images[currentIndex].src;
    imgEl.alt = images[currentIndex].alt || "";
  }

  function nextImg() { setImage(currentIndex + 1); }
  function prevImg() { setImage(currentIndex - 1); }

  // ----------------------------
  // Open / close
  // ----------------------------
  function openLightbox(index) {
    isOpen = true;
    lightbox.classList.remove("hidden");
    document.documentElement.classList.add("overflow-hidden");
    document.body.classList.add("overflow-hidden");

    setImage(index);
    requestAnimationFrame(() => btnClose.focus());
  }

  function closeLightbox() {
    isOpen = false;
    lightbox.classList.add("hidden");

    dragging = false;
    activePointerId = null;
    stage.classList.remove("is-dragging");

    document.documentElement.classList.remove("overflow-hidden");
    document.body.classList.remove("overflow-hidden");
    document.body.style.overflow = "";
  }

  // ----------------------------
  // Openers
  // ----------------------------
  openers.forEach((opener) => {
    opener.style.cursor = "zoom-in";
    opener.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
      openLightbox(findIndexForOpener(opener));
    }, true);
  });

  // ----------------------------
  // Buttons
  // ----------------------------
  btnNext.addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation(); nextImg(); });
  btnPrev.addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation(); prevImg(); });
  btnClose.addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation(); closeLightbox(); });

  lightbox.addEventListener("click", (e) => {
    if (e.target === lightbox) closeLightbox();
  });

  // ----------------------------
  // Wheel zoom
  // ----------------------------
  function onWheelZoom(e) {
    if (!isOpen) return;
    e.preventDefault();
    if (e.deltaY < 0) zoomIn();
    else zoomOut();
  }
  stage.addEventListener("wheel", onWheelZoom, { passive: false });
  imgEl.addEventListener("wheel", onWheelZoom, { passive: false });

  // ----------------------------
  // Drag to pan
  // ----------------------------
  function onPointerDown(e) {
    if (!isOpen) return;
    if (e.button !== undefined && e.button !== 0) return;
    if (!canPan()) return;

    e.preventDefault();
    dragging = true;
    activePointerId = e.pointerId;
    lastX = e.clientX;
    lastY = e.clientY;

    document.body.style.overflow = "hidden";
    stage.setPointerCapture?.(activePointerId);
    applyTransform();
  }

  function onPointerMove(e) {
    if (!dragging || e.pointerId !== activePointerId) return;
    e.preventDefault();

    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;

    offsetX += dx;
    offsetY += dy;

    clampOffsets();

    lastX = e.clientX;
    lastY = e.clientY;

    applyTransform();
  }

  function endDrag(e) {
    if (e && activePointerId != null && e.pointerId !== activePointerId) return;

    dragging = false;
    activePointerId = null;
    document.body.style.overflow = "";

    snapBack();
  }

  stage.addEventListener("pointerdown", onPointerDown, { passive: false });
  stage.addEventListener("pointermove", onPointerMove, { passive: false });
  stage.addEventListener("pointerup", endDrag);
  stage.addEventListener("pointercancel", endDrag);
  stage.addEventListener("lostpointercapture", endDrag);

  imgEl.addEventListener("pointerdown", onPointerDown, { passive: false });
  imgEl.addEventListener("pointermove", onPointerMove, { passive: false });
  imgEl.addEventListener("pointerup", endDrag);
  imgEl.addEventListener("pointercancel", endDrag);

  // Resize
  window.addEventListener("resize", () => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      const oldScale = scale;
      measureScales();
      // keep current scale but never below MIN_SCALE
      scale = Math.max(oldScale, MIN_SCALE);
      clampOffsets();
      applyTransform();
    });
  });

  // Keyboard
  document.addEventListener("keydown", (e) => {
    if (!isOpen) return;
    if (e.key === "Escape") closeLightbox();
    if (e.key === "ArrowRight") nextImg();
    if (e.key === "ArrowLeft") prevImg();
    if (e.key === "+" || e.key === "=") zoomIn();
    if (e.key === "-" || e.key === "_") zoomOut();
  });
});
